import * as React from 'react';
import { cva } from 'class-variance-authority';
import { Check, ChevronsUpDown } from 'lucide-react';

import { Button } from '~/shadcn/components/ui/Button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '~/shadcn/components/ui/Command';
import { Popover, PopoverContent, PopoverTrigger } from '~/shadcn/components/ui/Popover';
import { cn } from '~/shadcn/lib/utils';

const popoverContentVariants = cva('p-0', {
    variants: {
        popoverWidth: {
            trigger: 'w-[var(--radix-popover-trigger-width)]',
            content: 'w-fit min-w-[var(--radix-popover-trigger-width)]',
            custom: '',
        },
    },
    defaultVariants: {
        popoverWidth: 'content',
    },
});

function Combobox({
    align = 'center',
    className,
    closeOnSelect = true,
    commandProps,
    container,
    disabled,
    handleSelect,
    hideCheckmark = false,
    id,
    onSearchValueChange,
    options,
    placeholder = 'Select...',
    popoverWidth,
    triggerClassName,
    truncateLabel = false,
    value,
}) {
    const [open, setOpen] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState('');

    const commandListRef = React.useRef(null);

    React.useEffect(() => {
        if (commandListRef.current) {
            commandListRef.current.scrollTop = 0;
        }
    }, [searchValue]);

    const handleSearchChange = React.useCallback(
        (nextValue) => {
            setSearchValue(nextValue);
            if (typeof onSearchValueChange === 'function') {
                onSearchValueChange(nextValue);
            }
        },
        [onSearchValueChange],
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    id={id}
                    className={cn(
                        'w-content justify-between font-normal hover:cursor-pointer data-[state=open]:bg-accent data-[state=open]:text-accent-foreground',
                        triggerClassName,
                    )}
                    disabled={disabled}
                >
                    {(() => {
                        const selectedLabel = value
                            ? options.find((option) => option.value === value)?.label
                            : placeholder;
                        if (!truncateLabel) return selectedLabel;
                        return <span className="min-w-0 flex-1 truncate text-left">{selectedLabel}</span>;
                    })()}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align={align}
                container={container}
                className={cn(popoverContentVariants({ popoverWidth }), className)}
            >
                <Command {...commandProps}>
                    <CommandInput placeholder="Search..." onValueChange={handleSearchChange} />
                    <CommandList ref={commandListRef}>
                        <CommandEmpty>No results found.</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => (
                                <CommandItem
                                    key={option.value}
                                    value={option.value}
                                    onSelect={() => {
                                        handleSelect(option.value);
                                        if (closeOnSelect) {
                                            setOpen(false);
                                        }
                                    }}
                                >
                                    {!hideCheckmark && (
                                        <Check
                                            className={cn(
                                                'mr-2 h-4 w-4',
                                                value === option.value ? 'opacity-100' : 'opacity-0',
                                            )}
                                        />
                                    )}
                                    {option.label}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

export { Combobox };
